// @ow/shared — types and Zod schemas shared across server and web.
//
// This package is intentionally small. Full domain schemas (cohorts, profiles,
// segmentations, facts, etc.) land in a follow-up PR alongside the DB schema
// in packages/server/src/db/schema.ts so the two stay in lockstep.

import { z } from "zod";

// --- Geography ------------------------------------------------------------

export { REGION_LABELS, REGION_OF, type RegionLabel, regionOfIso3 } from "./regions.js";
export { iso2to3, iso3to2 } from "./iso.js";

// --- Tenancy --------------------------------------------------------------
// Two hostnames, one process, one DB — see tenant.ts.

export {
  DEFAULT_TENANT,
  FABRICATED_ISO3S,
  isTenantId,
  resolveTenant,
  TENANT_HEADER,
  TENANT_IDS,
  TENANTS,
  type TenantGateway,
  type TenantId,
  type TenantProfile,
  type TenantRegion,
  tenantFromHost,
  tenantProfile,
} from "./tenant.js";
// --- Provenance -----------------------------------------------------------
// What a published source label may say. Shared because the same stored string
// has to be cleaned on the server (read side) and in the web (render side).

export { publicSourceLabel } from "./provenance.js";
export { DEMO_SEGMENT_KEYS, demoSegmentLabel } from "./demo-segments.js";

// --- Saudi entry rules ----------------------------------------------------
// How a passport enters Saudi Arabia: which rules reach it, and which published
// category that resolves to. Shared so section 2.3, GTM 4.2, the printed brief
// and the assistant cannot grade or word the same market differently.
// See entry-rules.ts.

export {
  ENTRY_CATEGORIES,
  type EntryCategory,
  type EntryCategoryCarrier,
  type EntryCategoryMeta,
  type EntryRuleSelector,
  conditionalRoutes,
  entryCategories,
  entryCategoryMeta,
  entryCategoryOf,
  grantsConditionalRoute,
  isEasyEntry,
  publishedCategory,
  rulesForMarket,
} from "./entry-rules.js";

// --- Dashboard rankings ---------------------------------------------------
// Shared so the dashboard sections and the assistant's tools rank the same
// rows the same way. See rankings.ts.

export {
  type CagrRow,
  clearsConnectivitySeatFloor,
  CONNECTIVITY_DEST_MIN_SEATS,
  CONNECTIVITY_PAIR_BOARD_SIZE,
  type ConnectivityPairGrowthBoards,
  type ConnectivitySeatRow,
  connectivityPairGrowthBoards,
  connectivityPairSeatFloor,
  type SaudiConnectivityGrowthBoards,
  type SaudiConnectivityGrowthRow,
  saudiConnectivityGrowthBoards,
  roundToOneSignificant,
  connectivityPriorSeats,
  CORRIDOR_MIN_SIZE_MILLIONS,
  EXCLUDED_TREND_FRAGMENTS,
  isTrendsExcluded,
  TRENDS_EXCLUDED_ISO3,
  fastestGrowing,
  SENTIMENT_GAP_BOARD_SIZE,
  isEmergingTerm,
  isExcludedTrendLabel,
  GROWTH_BOARD_SIZE,
  INBOUND_MIN_ARRIVALS_MILLIONS,
  INBOUND_MIN_SPEND_BILLIONS,
  INBOUND_SPEND_PER_VISITOR_MIN_ARRIVALS_MILLIONS,
  INBOUND_UNIVERSE,
  INBOUND_MEASURES,
  DEFAULT_INBOUND_MEASURE,
  type InboundMeasure,
  type InboundMeasureKey,
  type InboundMeasureRow,
  isInboundMeasureKey,
  inboundMeasure,
  inboundMeasureValue,
  inboundMeasureCagr,
  inboundFloorText,
  inboundFloorReason,
  clearsInboundFloor,
  inboundGrowthBoards,
  inboundSpendPerVisitorBoard,
  spendPerVisitorUsd,
  SOURCE_MIN_SPEND_BILLIONS,
  SOURCE_MIN_TRIPS_MILLIONS,
  SOURCE_SPEND_PER_VISITOR_MIN_TRIPS_MILLIONS,
  clearsSourceFloor,
  sourceFloorText,
  sourceFloorReason,
  SAUDI_SOURCE_MIN_VISITORS_MILLIONS,
  clearsSaudiSourceFloor,
  saudiSourceFloorText,
  saudiSourceFloorReason,
  OUTBOUND_UNIVERSE,
  qualifyForGrowthBoard,
  DEFAULT_SAUDI_GROWTH_HORIZON,
  DEFAULT_SOURCE_GROWTH_HORIZON,
  type GrowthHorizon,
  type GrowthHorizonKey,
  isCovidTroughYear,
  isSaudiGrowthHorizonKey,
  isSourceGrowthHorizonKey,
  SAUDI_GROWTH_HORIZONS,
  type SaudiGrowthHorizon,
  type SaudiGrowthHorizonKey,
  type SaudiGrowthRow,
  saudiGrowthBaseYear,
  saudiGrowthBaseYears,
  saudiGrowthBoards,
  saudiGrowthHorizon,
  saudiSpendCagrPct,
  type SaudiTargetRow,
  saudiTargetBoards,
  saudiTargetProgressPct,
  SAUDI_TARGET_VIEWS,
  DEFAULT_SAUDI_TARGET_VIEW,
  type SaudiTargetView,
  isSaudiTargetView,
  SAUDI_TARGET_MIN_ACTUAL_MILLIONS,
  SAUDI_TARGET_MIN_VISITORS_MILLIONS,
  SAUDI_TARGET_ON_TRACK_PCT,
  SAUDI_TARGET_BEHIND_PCT,
  SAUDI_TARGET_TIERS,
  type SaudiTargetTier,
  saudiTargetTier,
  SAUDI_DESTINATION_MEASURES,
  DEFAULT_SAUDI_DESTINATION_MEASURE,
  type SaudiDestinationMeasure,
  type SaudiDestinationMeasureKey,
  isSaudiDestinationMeasureKey,
  SAUDI_DESTINATION_BASES,
  type SaudiDestinationBasis,
  type SaudiDestinationRow,
  saudiSpendPerVisitor,
  saudiDestinationValue,
  saudiDestinationGrowthPct,
  saudiDestinationRanking,
  SAUDI_EMERGING_MOMENTUM_MIN_SCORE,
  SAUDI_EMERGING_WALL_BUCKET,
  SAUDI_SEARCH_MIN_REL_VOLUME,
  SAUDI_SEARCH_SECTION_MIN_REL_VOLUME,
  SEARCH_MIN_REL_VOLUME,
  clearsSearchVolumeFloor,
  searchMomentumBoard,
  searchVolumeBoard,
  searchVolumeFloorNote,
  type SignalVolumeRow,
  slowestGrowing,
  SOURCE_GROWTH_HORIZONS,
  sourceGrowthBaseYear,
  sourceGrowthBoards,
  sourceGrowthHorizon,
  type SourceGrowthRow,
  SOURCE_MEASURES,
  DEFAULT_SOURCE_MEASURE,
  type SourceMeasureKey,
  type SourceSeriesRow,
  isSourceMeasureKey,
  sourceMeasure,
  sourceMeasureAt,
  sourceWindowRow,
  sourceSpendPerVisitorBoard,
  spendCagrPct,
  GTM_CONNECTIVITY_MARKETS,
  GTM_ROUTE_DEMAND_FLOOR,
  type GtmRouteGridRow,
  gtmRouteDemandShown,
  gtmRouteGridRowPublished,
  gtmRouteShortage,
} from "./rankings.js";

// --- Segmentation destination mentions ------------------------------------
// The Explorer's "Where they go" ranking, shared so the map's boards and the
// assistant's answer about the same cohort cannot differ. See
// destination-mentions.ts.

export {
  bottomBoardIsRanked,
  countryNameFromIso3,
  DESTINATION_BOARD_SIZE,
  type DestinationBoards,
  destinationBoards,
  type DestinationIntent,
  destinationIntent,
  type DestinationMentionRow,
  type RankedDestinationMentions,
  rankDestinationMentions,
} from "./destination-mentions.js";

// --- Prioritization opportunity by basis ----------------------------------
// The opportunity step reads by the ministry target, the structural share or
// the feasible share; the route, the section and the assistant size each the
// same way. See prioritization.ts.

export {
  DEFAULT_OPPORTUNITY_BASIS,
  OPPORTUNITY_BASES,
  type OpportunityBasis,
  type OpportunityBasisRow,
  isOpportunityBasis,
  opportunityBaseVolumeK,
  opportunityBasisLabel,
  opportunityBasisOptions,
  opportunityBasisPhrase,
  opportunityCarriesSpend,
  opportunitySharePct,
  opportunitySpendUsdM,
  opportunityVolumeK,
  rankByOpportunity,
} from "./prioritization.js";

// --- Segmentation trait normalisation -------------------------------------
// How every messy raw trait value folds into a published bucket, plus the
// dimension table both the Explorer's behaviour wall and the server's explorer
// rollup count from. See segmentation-traits.ts.

export {
  DIMENSION_BY_ID,
  SEGMENTATION_DIMENSIONS,
  type SegmentationDimension,
  type TraitDistribution,
  type TraitLayer,
  canonicalName,
  countTrait,
  meaningfulFrag,
  normalizeAccom,
  normalizeAge,
  normalizeBarriers,
  normalizeBooking,
  normalizeChannel,
  normalizeCompanion,
  normalizeDecisionRole,
  normalizeDuration,
  normalizeFrequency,
  normalizeHousehold,
  normalizeIncome,
  normalizeKeyword,
  normalizeLabel,
  normalizeLeadTime,
  normalizeObsWindow,
  normalizePremium,
  normalizeResearch,
  normalizeScope,
  normalizeSeason,
  normalizeSensitivity,
  normalizeSpend,
  normalizeSpendBasis,
  normalizeTransport,
  normalizeTriggers,
  normalizeTripsObserved,
  splitMulti,
} from "./segmentation-traits.js";

// --- Auth / session -------------------------------------------------------

export const ScopeSchema = z.enum(["readonly", "analyst", "admin"]);
export type Scope = z.infer<typeof ScopeSchema>;

export const SessionPayloadSchema = z.object({
  authenticated: z.boolean(),
  scopes: z.array(ScopeSchema),
  email: z.string().email().nullable(),
});
export type SessionPayload = z.infer<typeof SessionPayloadSchema>;

// --- Platforms ------------------------------------------------------------

export const PlatformSchema = z.enum(["reddit", "twitter", "instagram", "tripadvisor"]);
export type Platform = z.infer<typeof PlatformSchema>;

// --- STA segments ---------------------------------------------------------

export const SegmentSchema = z.enum([
  "drifters",
  "culturalists",
  "achievers",
  "growth_seekers",
  "flair_seekers",
  "bond_seekers",
  "elites",
  "faith_traveller",
  "unclassified",
]);
export type Segment = z.infer<typeof SegmentSchema>;

// --- Health endpoint ------------------------------------------------------

export const HealthResponseSchema = z.object({
  status: z.literal("ok"),
  service: z.literal("tq-traveler-segmentation"),
  version: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
